function isRichTextEditor(element) {
  return Boolean(
    element?.isContentEditable ||
    element?.contentEditable === 'true' ||
    element?.getAttribute?.('contenteditable') === 'true'
  );
}

const SELECTION_INPUT_TYPES = new Set(['text', 'search', 'tel', 'url', 'password']);

function supportsSelectionRange(element) {
  const tagName = String(element?.tagName || '').toLowerCase();
  if (tagName === 'textarea') return true;
  if (tagName !== 'input') return false;
  return SELECTION_INPUT_TYPES.has(String(element.type || 'text').toLowerCase());
}

export function setEditorCaret(element, requestedOffset) {
  if (!element) return false;

  const offset = Math.max(0, Number(requestedOffset) || 0);
  if (supportsSelectionRange(element) && typeof element.setSelectionRange === 'function') {
    const end = Math.min(offset, String(element.value || '').length);
    element.focus?.();
    try {
      element.setSelectionRange(end, end);
      return true;
    } catch {
      return false;
    }
  }

  if (!isRichTextEditor(element) || typeof document.createRange !== 'function') {
    return false;
  }

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }

  if (textNodes.length === 0) {
    const emptyRange = document.createRange();
    emptyRange.selectNodeContents(element);
    emptyRange.collapse(false);
    const emptySelection = window.getSelection?.();
    if (!emptySelection) return false;
    emptySelection.removeAllRanges();
    emptySelection.addRange(emptyRange);
    element.focus?.();
    return true;
  }

  let remaining = offset;
  let targetNode = textNodes[textNodes.length - 1];
  let targetOffset = targetNode.textContent?.length || 0;
  let protectedBoundary = null;
  let caretResolved = false;
  const visitedProtectedNodes = new Set();

  for (let index = 0; index < textNodes.length; index += 1) {
    const textNode = textNodes[index];
    const protectedNode = textNode.parentElement?.closest?.('[contenteditable="false"]');
    if (protectedNode && element.contains(protectedNode)) {
      if (visitedProtectedNodes.has(protectedNode)) continue;
      visitedProtectedNodes.add(protectedNode);
      const protectedLength = protectedNode.textContent?.length || 0;
      if (remaining === 0) {
        protectedBoundary = { node: protectedNode, position: 'before' };
        caretResolved = true;
        break;
      }
      if (remaining <= protectedLength) {
        protectedBoundary = { node: protectedNode, position: 'after' };
        caretResolved = true;
        break;
      }
      remaining -= protectedLength;
      continue;
    }

    const length = textNode.textContent?.length || 0;
    if (remaining <= length) {
      targetNode = textNode;
      targetOffset = remaining;
      caretResolved = true;
      break;
    }
    remaining -= length;
  }

  const range = document.createRange();
  if (!caretResolved) {
    range.selectNodeContents(element);
    range.collapse(false);
  } else if (protectedBoundary?.position === 'before') {
    range.setStartBefore(protectedBoundary.node);
  } else if (protectedBoundary?.position === 'after') {
    range.setStartAfter(protectedBoundary.node);
  } else {
    range.setStart(targetNode, Math.min(targetOffset, targetNode.textContent?.length || 0));
  }
  if (caretResolved) range.collapse(true);

  const selection = window.getSelection?.();
  if (!selection) return false;
  selection.removeAllRanges();
  selection.addRange(range);
  element.focus?.();
  return true;
}
